import { useRef, useEffect, useMemo } from 'react'
import * as echarts from 'echarts/core'
import { BarChart } from 'echarts/charts'
import { GridComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useTheme, alpha } from '@mui/material/styles'
import { chronological, fmtCount } from './vocabulary'

echarts.use([BarChart, GridComponent, TooltipComponent, CanvasRenderer])

// One stacked bar per UTC day. Both bar charts on this page are this shape —
// the durability stack (totalling the day's declared spans) and the
// never-durable breakdown (totalling that day's losses) — and they differ
// only in which series they stack. Keeping them one component is also what
// keeps their colors from drifting apart.
//
// The second chart exists at all because of scale: on a healthy fleet the
// losses are single digits against thousands of durable spans, and stacked
// into the first chart they would be a sub-pixel sliver. Given its own
// y-axis, three lost spans is a bar you can see.
//
// `formatValue`, `formatAxis`, `emptyText` and `minInterval` let a page that is
// not counting spans (the Cost page's dollars) reuse it; the defaults are
// this page's.
const StackedDayChart = ({
  days,
  series,
  height = 240,
  selectedDay,
  onSelectDay,
  highlighted,
  formatValue = fmtCount,
  formatAxis,
  emptyText = 'No spans declared in this window.',
  minInterval = 1,
  fadedDay,
}) => {
  const containerRef = useRef(null)
  const chartRef = useRef(null)
  const onSelectRef = useRef(onSelectDay)
  onSelectRef.current = onSelectDay
  const theme = useTheme()

  const rows = useMemo(() => chronological(days), [days])
  // The empty state renders a different element and never attaches the ref,
  // so there is no canvas to initialise until there is data. Keyed on this
  // rather than returning early above, which would make the hooks
  // conditional: the effect then runs on the render that first draws the
  // container, and tears down again if the window goes back to empty.
  const isEmpty = !days.length

  useEffect(() => {
    if (isEmpty || !containerRef.current) return undefined
    const chart = echarts.init(containerRef.current)
    chartRef.current = chart

    chart.on('click', ({ name }) => onSelectRef.current?.(name))

    const observer = new ResizeObserver(() => chart.resize())
    observer.observe(containerRef.current)
    return () => {
      observer.disconnect()
      chart.dispose()
      chartRef.current = null
    }
  }, [isEmpty])

  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return

    const textColor = theme.palette.text.secondary
    const gridColor = alpha(textColor, 0.15)

    const tooltipFormatter = (params) => {
      const total = params.reduce((sum, { value }) => sum + (value || 0), 0)
      const lines = params
        .filter(({ value }) => value)
        .reverse()
        .map(
          ({ marker, seriesName, value }) =>
            `<div style="display:flex;justify-content:space-between;gap:18px;">
              <span>${marker}${seriesName}</span><b>${formatValue(value)}</b>
            </div>`
        )
        .join('')
      return `<div style="min-width:170px">
        <div style="margin-bottom:4px;">${params[0].axisValueLabel} UTC</div>
        ${lines || '<div style="opacity:.6">nothing</div>'}
        <div style="display:flex;justify-content:space-between;gap:18px;
          border-top:1px solid rgba(255,255,255,0.25);margin-top:4px;padding-top:4px;">
          <span>Total</span><b>${formatValue(total)}</b>
        </div>
      </div>`
    }

    chart.setOption(
      {
        animationDuration: 500,
        animationDurationUpdate: 300,
        grid: { left: 52, right: 16, top: 12, bottom: 24 },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' },
          backgroundColor: 'rgba(33, 33, 41, 0.94)',
          borderWidth: 0,
          textStyle: { color: '#fff', fontSize: 12 },
          formatter: tooltipFormatter,
        },
        xAxis: {
          type: 'category',
          data: rows.map(({ day }) => day),
          axisLabel: {
            color: textColor,
            fontSize: 10,
            // A 90-day window cannot label every bar; ECharts drops labels
            // on its own, and the tooltip carries the exact day anyway.
            formatter: (day) => day.slice(5),
          },
          axisLine: { lineStyle: { color: gridColor } },
          axisTick: { show: false },
        },
        yAxis: {
          type: 'value',
          minInterval,
          axisLabel: { color: textColor, fontSize: 10, formatter: formatAxis },
          splitLine: { lineStyle: { color: gridColor } },
        },
        series: series.map(({ key, label, color, valueOf }) => ({
          name: label,
          type: 'bar',
          stack: 'day',
          // One source of truth for the series color; the points below
          // vary opacity only. The page's legend reads the same value, so a
          // swatch and its bars cannot drift apart.
          color,
          data: rows.map((row) => ({
            value: valueOf(row),
            // The selected day keeps full opacity; the rest dim, so the
            // detail panel below is always visibly about one bar.
            // `fadedDay` (the Cost page's today, still filling) draws
            // faded the same way, selected or not.
            itemStyle:
              (selectedDay && row.day !== selectedDay) || row.day === fadedDay
                ? { opacity: 0.35 }
                : undefined,
          })),
          barMaxWidth: 34,
          emphasis: { focus: 'series' },
          cursor: 'pointer',
        })),
      },
      // Replaced, not merged: the series set is fixed but a window change
      // swaps every category, and a merge would leave the old days' bars
      // behind the new ones.
      { replaceMerge: ['series'] }
    )
  }, [
    rows,
    series,
    selectedDay,
    theme,
    formatValue,
    formatAxis,
    minInterval,
    fadedDay,
  ])

  // Hovering the page's legend emphasises that series and blurs the rest —
  // both charts set `emphasis: { focus: 'series' }`, so ECharts does the
  // blurring; this only has to say which one. Declared after the option
  // effect so a fresh setOption never lands on top of a live highlight.
  useEffect(() => {
    const chart = chartRef.current
    if (!chart) return undefined
    const entry = highlighted
      ? series.find((candidate) => candidate.key === highlighted)
      : null
    if (!entry) {
      chart.dispatchAction({ type: 'downplay' })
      return undefined
    }
    chart.dispatchAction({ type: 'highlight', seriesName: entry.label })
    return () =>
      chart.dispatchAction({ type: 'downplay', seriesName: entry.label })
  }, [highlighted, series])

  return (
    <Box sx={{ position: 'relative', width: '100%', height }}>
      {/* ECharts owns this node's children outright — it fills them on
          init and clears them on dispose. React must therefore never
          render anything into it, and never reuse it for anything else:
          the empty state is a *sibling* rather than the other branch of a
          ternary, because a ternary lets React reconcile the two as the
          same div, hand it to ECharts, and then fail to remove a child
          ECharts had already cleared away (the crash of 2026-09-14). */}
      <Box
        ref={containerRef}
        sx={{
          width: '100%',
          height,
          visibility: isEmpty ? 'hidden' : 'visible',
        }}
      />
      {isEmpty && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {emptyText}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default StackedDayChart
